import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Favorite} from '@models/favorite';
import {AuthenticationService} from '@services/authentification.service';
import {HttpClient} from '@angular/common/http';
import {CityService} from '@services/city.service';
import {AdService} from '@services/ad.service';
import {MatDialog} from '@angular/material/dialog';
import {EditCaracComponent} from '@components/popup/edit-carac/edit-carac.component';
import {EditAnnexeComponent} from '@components/popup/edit-annexe/edit-annexe.component';
import {EditOtherCaracComponent} from '@components/popup/edit-other-carac/edit-other-carac.component';
import {EditDiagComponent} from '@components/popup/edit-diag/edit-diag.component';
import {EditDescriptionComponent} from '@components/popup/edit-description/edit-description.component';

@Component({
    selector: 'app-widget-descriptif',
    templateUrl: './descriptif.component.html',
    styleUrls: ['./descriptif.component.scss']
})
export class DescriptifComponent implements OnInit {

    @Input() favorite: Favorite;
    @Input() preview: boolean;
    @Output() favoriteChange = new EventEmitter();

    genericLink: string = null;
    displayDescription: string = '';

    // Masquage par priorité : une annonce dédoublonnée peut regrouper plusieurs sources
    // dans favorite.ad.urls. On masque une source dès qu'une source de meilleure qualité
    // est disponible pour la même annonce.
    // Rang faible = masqué en priorité. Ordre : leboncoin > seloger > bienici > century21/guyhoquet/citya.
    // Les providers absents de cette table ne sont jamais masqués (rang = infini).
    private readonly providerHideRank: { [provider: string]: number } = {
        leboncoin: 1,
        seloger: 2,
        bienici: 3,
        century21: 4,
        guyhoquet: 4,
        citya: 4,
    };

    constructor(
        public dialog: MatDialog,
        private http: HttpClient,
        public cityService: CityService,
        public adService: AdService,
        public authenticationService: AuthenticationService) {
    }

    // Rang de qualité d'un provider (infini si non listé, donc jamais masqué).
    private rankOf(provider: string): number {
        const rank = this.providerHideRank[provider];
        return rank === undefined ? Number.POSITIVE_INFINITY : rank;
    }

    // Vrai s'il faut masquer ce lien, càd s'il existe dans la même annonce une source
    // de meilleur rang (rang strictement supérieur).
    shouldHideLink(link: { provider: string }, urls: Array<{ provider: string }>): boolean {
        const rank = this.providerHideRank[link.provider];
        if (rank === undefined) {
            return false;
        }
        return (urls || []).some(other => this.rankOf(other.provider) > rank);
    }

    ngOnInit() {
        this.favorite.ad.description = this.favorite.ad.description.replace(/\\r\\n\\r/gm, '<br/>').replace(/\\n/gm, '<br/>').replace(/\\r/gm, '');

        this.refreshDisplayDescription();
    }

    // Favori manuel (ad_id === 0) : si la description commence par un lien suivi de \n\n,
    // on extrait ce lien pour l'afficher comme un bouton générique. On ne masque que l'affichage,
    // sans modifier favorite.description (sinon le lien serait perdu au prochain update).
    refreshDisplayDescription() {
        this.displayDescription = this.favorite.description;
        if (this.favorite.ad_id === 0 && this.favorite.description) {
            const match = this.favorite.description.match(/^\s*(https?:\/\/[^\s\\]+)(?:\\n\\n|\r?\n\r?\n)/);
            if (match) {
                this.genericLink = match[1];
                this.displayDescription = this.favorite.description.substring(match[0].length);
            }
        }
    }

    editCarac() {
        const dialogRef = this.dialog.open(EditCaracComponent, {
            width: '600px',
            data: {favorite: this.favorite},
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined) {
                this.favorite.estate_sale = result.estate_sale;
                this.favorite.has_copro = result.has_copro;
                this.favorite.lot_copro = result.lot_copro;
                this.favorite.floor = result.floor;
                this.favorite.floor_building = result.floor_building;
                this.favorite.year_building = result.year_building;
                this.favorite.orientation = result.orientation;
                this.favorite.heating = result.heating;
                this.favorite = this.adService.updateValues(this.favorite);
                this.favoriteChange.emit();
            }
        });
    }

    editAnnexe() {
        const dialogRef = this.dialog.open(EditAnnexeComponent, {
            width: '600px',
            data: {favorite: this.favorite},
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined) {
                this.favorite.bedrooms = result.bedrooms;
                this.favorite.restrooms = result.restrooms;
                this.favorite.bathrooms = result.bathrooms;
                this.favorite.shower_rooms = result.shower_rooms;
                this.favorite.parking = result.parking;
                this.favorite.pool = result.pool;
                this.favorite.cellar = result.cellar;
                this.favorite.outside = result.outside;
                this.favorite = this.adService.updateValues(this.favorite);
                this.favoriteChange.emit();
            }
        });
    }

    editOther() {
        const dialogRef = this.dialog.open(EditOtherCaracComponent, {
            width: '600px',
            data: {favorite: this.favorite},
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined) {
                this.favorite.elevator = result.elevator;
                this.favorite.urgent = result.urgent;
                this.favorite.rent = result.rent;
                this.favorite.work = result.work;
                this.favorite.renov = result.renov;
                this.favorite.owner_type = result.owner_type;
                this.favorite.disabled_access = result.disabled_access;
                this.favorite.guardian = result.guardian;
                this.favorite.intercom = result.intercom;
                this.favorite.closed_view = result.closed_view;
                this.favorite.digital_code = result.digital_code;
                this.favorite = this.adService.updateValues(this.favorite);
                this.favoriteChange.emit();
            }
        });
    }

    editDescription() {
        const dialogRef = this.dialog.open(EditDescriptionComponent, {
            width: '600px',
            data: {favorite: this.favorite},
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined) {
                // La popup édite favorite.description (champ envoyé au back), pas ad.description
                this.favorite.description = result.description;
                this.favorite = this.adService.updateValues(this.favorite);
                this.refreshDisplayDescription();
                this.favoriteChange.emit();
            }
        });
    }

    editDiag() {
        const dialogRef = this.dialog.open(EditDiagComponent, {
            width: '600px',
            data: {favorite: this.favorite},
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined) {
                this.favorite.energy_rate = result.energy_rate;
                this.favorite.energy_rate_value = result.energy_rate_value;
                this.favorite.ges = result.ges;
                this.favorite.ges_value = result.ges_value;
                this.favorite.ref_ademe = result.ref_ademe;
                this.favorite = this.adService.updateValues(this.favorite);
                this.favoriteChange.emit();
            }
        });
    }
}
